import React, { useState, useEffect, useMemo } from 'react';
import { taskBatchAPI } from '../../../api/services';
import type { TaskBatch } from '../../../types';

interface Props {
  projectId: string;
  projectName: string;
  tasks: any[];
  bugs: any[];
  features: any[];
  onClose: () => void;
  onSubmitted: () => void;
}

type Stage = 'select' | 'review' | 'submitted';

const BatchSubmitModal: React.FC<Props> = ({ projectId, projectName, tasks, bugs, features, onClose, onSubmitted }) => {
  const [stage, setStage] = useState<Stage>('select');
  const [batchName, setBatchName] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState<TaskBatch | null>(null);
  const [itemEdits, setItemEdits] = useState<Record<string, number>>({});
  // Maps itemId → batch name for items in active (non-draft) batches
  const [pendingBatchMap, setPendingBatchMap] = useState<Record<string, string>>({});

  useEffect(() => {
    taskBatchAPI.getByProject(projectId).then(res => {
      const batches: TaskBatch[] = res.data?.data || [];
      const map: Record<string, string> = {};
      // Only block on pending/disputed, not drafts (backend cleans those up)
      batches.filter(b => b.approvalStatus === 'pending_approval' || b.approvalStatus === 'disputed')
        .forEach(b => b.items.forEach(i => { map[i.itemId] = b.name; }));
      setPendingBatchMap(map);
      // Auto-number: count non-draft batches
      const existingCount = batches.filter(b => b.approvalStatus !== 'draft').length;
      setBatchName(`${projectName} — Batch #${existingCount + 1}`);
    }).catch(() => {
      setBatchName(`${projectName} — Batch #1`);
    });
  }, [projectId, projectName]);

  // Tasks under a feature are only batch-eligible if needsBatchApproval is true
  // (i.e. they were added AFTER the feature was submitted for approval).
  // Floating tasks (no featureId) are always eligible.
  const isBatchEligible = (t: any) =>
    !t.deletedAt && !pendingBatchMap[t._id] && !t.batchId &&
    (!t.featureId || t.needsBatchApproval);

  const eligibleTasks = useMemo(() =>
    tasks.filter(t => (t.storyPoints === 0 || !t.storyPoints) && isBatchEligible(t)),
    [tasks, pendingBatchMap]
  );

  const eligibleBugs = useMemo(() =>
    bugs.filter(b => (b.storyPoints === 0 || !b.storyPoints) && !pendingBatchMap[b._id] && !(b as any).batchId),
    [bugs, pendingBatchMap]
  );

  type GroupItem = { id: string; type: 'task' | 'bug'; name: string; priority?: string; sp: number };

  const estimatedItems = useMemo(() => {
    const items: GroupItem[] = [];
    tasks.filter(t => (t.storyPoints || 0) > 0 && isBatchEligible(t))
      .forEach(t => items.push({ id: t._id, type: 'task', name: t.title || t.name, priority: t.priority, sp: t.storyPoints }));
    bugs.filter(b => (b.storyPoints || 0) > 0 && !pendingBatchMap[b._id] && !(b as any).batchId)
      .forEach(b => items.push({ id: b._id, type: 'bug', name: b.title || b.name, priority: b.severity, sp: b.storyPoints }));
    return items;
  }, [tasks, bugs, pendingBatchMap]);

  // Items blocked because they're in a pending/disputed batch
  const pendingItems = useMemo(() => {
    const items: { id: string; type: 'task' | 'bug'; name: string; batchName: string }[] = [];
    tasks.filter(t => (t.storyPoints === 0 || !t.storyPoints) && !t.deletedAt && pendingBatchMap[t._id])
      .forEach(t => items.push({ id: t._id, type: 'task', name: t.title || t.name, batchName: pendingBatchMap[t._id] }));
    bugs.filter(b => (b.storyPoints === 0 || !b.storyPoints) && pendingBatchMap[b._id])
      .forEach(b => items.push({ id: b._id, type: 'bug', name: b.title || b.name, batchName: pendingBatchMap[b._id] }));
    return items;
  }, [tasks, bugs, pendingBatchMap]);

  const featureMap = useMemo(() => {
    const map: Record<string, string> = {};
    features.forEach((f: any) => { map[f._id] = f.name; });
    return map;
  }, [features]);

  const grouped = useMemo(() => {
    const groups: { label: string; items: GroupItem[] }[] = [];

    const projectTasks = eligibleTasks.filter(t => !t.featureId);
    if (projectTasks.length > 0) {
      groups.push({
        label: 'Project tasks',
        items: projectTasks.map(t => ({ id: t._id, type: 'task' as const, name: t.title || t.name, priority: t.priority, sp: t.storyPoints || 0 })),
      });
    }

    const featureGroups: Record<string, any[]> = {};
    eligibleTasks.filter(t => t.featureId).forEach(t => {
      const fid = typeof t.featureId === 'object' ? t.featureId._id : t.featureId;
      if (!featureGroups[fid]) featureGroups[fid] = [];
      featureGroups[fid].push(t);
    });
    Object.entries(featureGroups).forEach(([fid, fTasks]) => {
      groups.push({
        label: featureMap[fid] || 'Feature tasks',
        items: fTasks.map(t => ({ id: t._id, type: 'task' as const, name: t.title || t.name, priority: t.priority, sp: t.storyPoints || 0 })),
      });
    });

    if (eligibleBugs.length > 0) {
      groups.push({
        label: 'Bugs',
        items: eligibleBugs.map(b => ({ id: b._id, type: 'bug' as const, name: b.title || b.name, priority: b.severity, sp: b.storyPoints || 0 })),
      });
    }
    return groups;
  }, [eligibleTasks, eligibleBugs, featureMap]);


  const totalEligible = grouped.reduce((s, g) => s + g.items.length, 0);
  const totalSelectable = totalEligible + estimatedItems.length;

  const toggleItem = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleGroup = (items: { id: string }[]) => {
    const allSelected = items.every(i => selectedIds.has(i.id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      items.forEach(i => allSelected ? next.delete(i.id) : next.add(i.id));
      return next;
    });
  };

  const selectAll = () => {
    const allItems = [...grouped.flatMap(g => g.items), ...estimatedItems];
    const allSelected = allItems.every(i => selectedIds.has(i.id));
    setSelectedIds(allSelected ? new Set() : new Set(allItems.map(i => i.id)));
  };

  const itemNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    tasks.forEach((t: any) => { map[t._id] = t.title || t.name || t._id; });
    bugs.forEach((b: any) => { map[b._id] = b.title || b.name || b._id; });
    return map;
  }, [tasks, bugs]);

  // Step 1: Send to AI → get draft back with estimates
  const handleEstimate = async () => {
    if (selectedIds.size === 0) return;
    setLoading(true);
    setError('');
    try {
      const allEligible = grouped.flatMap(g => g.items);
      const selectedEligible = allEligible.filter(i => selectedIds.has(i.id));
      const selectedEstimated = estimatedItems.filter(i => selectedIds.has(i.id));

      const items = [
        ...selectedEligible.map(i => ({ itemId: i.id, itemType: i.type })),
        ...selectedEstimated.map(i => ({ itemId: i.id, itemType: i.type })),
      ];

      // Pre-estimated points map for items that already have SP
      const preEstimatedPoints: Record<string, number> = {};
      selectedEstimated.forEach(i => { preEstimatedPoints[i.id] = i.sp; });

      const res = await taskBatchAPI.createDraft({ projectId, name: batchName, items, preEstimatedPoints });
      const batchData: TaskBatch = res.data?.data;
      setDraft(batchData);
      const edits: Record<string, number> = {};
      batchData.aiEstimation?.items.forEach(item => { edits[item.itemId] = item.points; });
      setItemEdits(edits);
      setStage('review');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to estimate batch');
    }
    setLoading(false);
  };

  // Step 2: Confirm submit with edited points
  const handleConfirmSubmit = async () => {
    if (!draft) return;
    setLoading(true);
    setError('');
    try {
      const res = await taskBatchAPI.confirmSubmit(draft._id, itemEdits);
      setDraft(res.data?.data);
      setStage('submitted');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit batch');
    }
    setLoading(false);
  };

  const editedTotal = useMemo(() => Object.values(itemEdits).reduce((s, v) => s + v, 0), [itemEdits]);

  const PRIORITY_DOT: Record<string, string> = { low: 'bg-green-400', medium: 'bg-yellow-400', high: 'bg-orange-400', critical: 'bg-red-500' };

  const STAGE_STEPS = [
    { key: 'select', label: 'Select items' },
    { key: 'review', label: 'Review estimates' },
    { key: 'submitted', label: 'Submitted' },
  ];
  const stageIdx = STAGE_STEPS.findIndex(s => s.key === stage);

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50 animate-[fadeIn_200ms_ease]" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div
          className="bg-white rounded-2xl shadow-2xl w-[90vw] max-w-2xl max-h-[90vh] flex flex-col animate-[slideUp_200ms_ease]"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">
                  {stage === 'select' ? 'Select items for approval' : stage === 'review' ? 'Review AI estimates' : 'Batch submitted'}
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">{projectName}</p>
              </div>
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            {/* Step indicator */}
            <div className="flex items-center gap-1">
              {STAGE_STEPS.map((s, i) => (
                <React.Fragment key={s.key}>
                  <div className="flex items-center gap-1.5">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium ${
                      i < stageIdx ? 'bg-blue-600 text-white' : i === stageIdx ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-400'
                    }`}>
                      {i < stageIdx ? (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                      ) : i + 1}
                    </div>
                    <span className={`text-[10px] ${i === stageIdx ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>{s.label}</span>
                  </div>
                  {i < STAGE_STEPS.length - 1 && <div className={`flex-1 h-px mx-1 ${i < stageIdx ? 'bg-blue-600' : 'bg-gray-200'}`} />}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-auto">
            {/* ─── Stage 1: Select ─── */}
            {stage === 'select' && (
              <div className="p-5">
                <div className="mb-4">
                  <label className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Batch name</label>
                  <input
                    value={batchName}
                    onChange={e => setBatchName(e.target.value)}
                    className="mt-1 w-full bg-gray-100 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-gray-300"
                    placeholder="Batch name"
                  />
                </div>

                {totalSelectable === 0 ? (
                  <p className="text-center py-12 text-sm text-gray-400">No eligible items — all tasks and bugs are in a pending batch</p>
                ) : (
                  <>
                    {totalEligible > 0 && (
                      <>
                        <div className="flex items-center justify-between mb-3">
                          <button onClick={selectAll} className="text-xs text-blue-600 hover:underline">
                            {grouped.flatMap(g => g.items).every(i => selectedIds.has(i.id)) ? 'Deselect all' : 'Select all'}
                          </button>
                          <span className="text-xs text-gray-400">{selectedIds.size} selected</span>
                        </div>

                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Needs estimation</span>
                          <span className="text-[10px] text-gray-300">{totalEligible}</span>
                        </div>

                        {grouped.map(group => (
                          <div key={group.label} className="mb-4">
                            <div className="flex items-center gap-2 mb-1.5">
                              <button onClick={() => toggleGroup(group.items)} className="flex items-center gap-1.5">
                                <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors ${
                                  group.items.every(i => selectedIds.has(i.id)) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                                }`}>
                                  {group.items.every(i => selectedIds.has(i.id)) && (
                                    <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                  )}
                                </div>
                                <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{group.label}</span>
                              </button>
                              <span className="text-[10px] text-gray-300">{group.items.length}</span>
                            </div>
                            {group.items.map(item => (
                              <div
                                key={item.id}
                                onClick={() => toggleItem(item.id)}
                                className="flex items-center gap-2.5 px-2 py-2 border-b border-gray-50 hover:bg-gray-50/80 cursor-pointer transition-colors"
                              >
                                <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-colors ${
                                  selectedIds.has(item.id) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                                }`}>
                                  {selectedIds.has(item.id) && (
                                    <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                  )}
                                </div>
                                {item.type === 'bug' && (
                                  <svg className="w-3 h-3 text-red-400 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6.56 1.14a.75.75 0 01.177 1.045 3.989 3.989 0 00-.464.86c.185.17.382.329.59.473A3.993 3.993 0 0110 2.75c1.244 0 2.356.568 3.098 1.457.183-.13.357-.272.513-.427a4.03 4.03 0 00-.517-1.07.75.75 0 011.233-.855c.372.534.636 1.15.77 1.812a.75.75 0 01-.3.79 5.477 5.477 0 01-1.024.582 4 4 0 01.134 1.011h1.843a.75.75 0 010 1.5h-1.88a4.014 4.014 0 01-.6 1.267l1.845 1.845a.75.75 0 11-1.06 1.06l-1.53-1.529A3.99 3.99 0 0110 11.25a3.99 3.99 0 01-2.523-.892l-1.53 1.53a.75.75 0 01-1.06-1.061l1.844-1.845a4.013 4.013 0 01-.6-1.267H4.25a.75.75 0 010-1.5h1.843a4 4 0 01.134-1.011 5.478 5.478 0 01-1.024-.581.75.75 0 01-.3-.79c.133-.663.397-1.279.77-1.813a.75.75 0 011.045-.177z" clipRule="evenodd" /></svg>
                                )}
                                <span className="text-sm text-gray-800 truncate flex-1">{item.name}</span>
                                {item.priority && (
                                  <span className={`w-2 h-2 rounded-full shrink-0 ${PRIORITY_DOT[item.priority] || 'bg-gray-300'}`} />
                                )}
                                <span className="text-[10px] text-gray-300 shrink-0">{item.type}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </>
                    )}
                  </>
                )}

                {/* Items that already have story points (selectable, skip AI) */}
                {estimatedItems.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Already estimated</span>
                      <span className="text-[10px] text-gray-300">{estimatedItems.length}</span>
                    </div>
                    <p className="text-[10px] text-gray-400 mb-2">These items already have story points. Select to include them in this batch (no AI estimation needed).</p>
                    {estimatedItems.map(item => (
                      <div
                        key={item.id}
                        onClick={() => toggleItem(item.id)}
                        className="flex items-center gap-2.5 px-2 py-2 border-b border-gray-50 hover:bg-gray-50/80 cursor-pointer transition-colors"
                      >
                        <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-colors ${
                          selectedIds.has(item.id) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                        }`}>
                          {selectedIds.has(item.id) && (
                            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                          )}
                        </div>
                        {item.type === 'bug' && (
                          <svg className="w-3 h-3 text-red-400 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6.56 1.14a.75.75 0 01.177 1.045 3.989 3.989 0 00-.464.86c.185.17.382.329.59.473A3.993 3.993 0 0110 2.75c1.244 0 2.356.568 3.098 1.457.183-.13.357-.272.513-.427a4.03 4.03 0 00-.517-1.07.75.75 0 011.233-.855c.372.534.636 1.15.77 1.812a.75.75 0 01-.3.79 5.477 5.477 0 01-1.024.582 4 4 0 01.134 1.011h1.843a.75.75 0 010 1.5h-1.88a4.014 4.014 0 01-.6 1.267l1.845 1.845a.75.75 0 11-1.06 1.06l-1.53-1.529A3.99 3.99 0 0110 11.25a3.99 3.99 0 01-2.523-.892l-1.53 1.53a.75.75 0 01-1.06-1.061l1.844-1.845a4.013 4.013 0 01-.6-1.267H4.25a.75.75 0 010-1.5h1.843a4 4 0 01.134-1.011 5.478 5.478 0 01-1.024-.581.75.75 0 01-.3-.79c.133-.663.397-1.279.77-1.813a.75.75 0 011.045-.177z" clipRule="evenodd" /></svg>
                        )}
                        <span className="text-sm text-gray-800 truncate flex-1">{item.name}</span>
                        <span className="text-xs text-blue-600 font-medium tabular-nums shrink-0">{item.sp} SP</span>
                        {item.priority && (
                          <span className={`w-2 h-2 rounded-full shrink-0 ${PRIORITY_DOT[item.priority] || 'bg-gray-300'}`} />
                        )}
                        <span className="text-[10px] text-gray-300 shrink-0">{item.type}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Items in pending batches (non-selectable) */}
                {pendingItems.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">In pending batch</span>
                      <span className="text-[10px] text-gray-300">{pendingItems.length}</span>
                    </div>
                    {pendingItems.map(item => (
                      <div key={item.id} className="flex items-center gap-2.5 px-2 py-2 border-b border-gray-50 opacity-50">
                        <div className="w-3.5 h-3.5 shrink-0" />
                        <span className="text-sm text-gray-500 truncate flex-1">{item.name}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-600 shrink-0 truncate max-w-[140px]">{item.batchName}</span>
                      </div>
                    ))}
                  </div>
                )}


                {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
              </div>
            )}

            {/* ─── Stage 2: Review AI Estimates ─── */}
            {stage === 'review' && draft && (
              <div className="p-5">
                {/* Summary cards */}
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-semibold text-blue-600 tabular-nums">{editedTotal}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Total SP</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-semibold text-gray-800 tabular-nums">{draft.items.length}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Items</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium inline-block">Draft</span>
                    <p className="text-[10px] text-gray-400 mt-1.5">Not yet submitted</p>
                  </div>
                </div>

                {/* Per-item estimate list with editable SP */}
                <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-2">
                  AI Estimation — adjust if needed
                </p>

                {draft.aiEstimation?.items.map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5 px-2 py-2.5 border-b border-gray-50">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${
                      item.itemType === 'bug' ? 'bg-red-50 text-red-500' : 'bg-gray-100 text-gray-500'
                    }`}>{item.itemType}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 truncate">{itemNameMap[item.itemId] || item.itemId}</p>
                      {item.reasoning && <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{item.reasoning}</p>}
                    </div>
                    <input
                      type="number"
                      min={0}
                      value={itemEdits[item.itemId] ?? item.points}
                      onChange={e => setItemEdits(prev => ({ ...prev, [item.itemId]: Math.max(0, Number(e.target.value) || 0) }))}
                      className="w-16 text-right text-sm font-medium text-blue-600 bg-gray-50 rounded px-2 py-1.5 outline-none focus:ring-1 focus:ring-gray-300 tabular-nums"
                    />
                  </div>
                ))}

                {draft.aiEstimation?.model && (
                  <p className="text-[10px] text-gray-300 mt-2">Model: {draft.aiEstimation.model}</p>
                )}

                {error && <p className="text-xs text-red-500 mt-3">{error}</p>}
              </div>
            )}

            {/* ─── Stage 3: Submitted ─── */}
            {stage === 'submitted' && draft && (
              <div className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                    <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <span className="text-sm font-medium text-gray-900">Batch submitted for approval</span>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-5">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-semibold text-blue-600 tabular-nums">{editedTotal}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Total SP</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-semibold text-gray-800 tabular-nums">{draft.items.length}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Items</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium inline-block">Pending Approval</span>
                    <p className="text-[10px] text-gray-400 mt-1.5">Status</p>
                  </div>
                </div>

                <p className="text-xs text-gray-400 text-center">Approvers have been notified. You can track this batch in the Approvals tab.</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <div>
              {stage === 'review' && (
                <button onClick={() => { setStage('select'); setDraft(null); setError(''); }}
                  className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
                  Back
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {stage === 'select' && (
                <>
                  <button onClick={onClose} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors">Cancel</button>
                  <button
                    onClick={handleEstimate}
                    disabled={selectedIds.size === 0 || loading}
                    className="px-4 py-1.5 text-xs font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? (
                      <span className="flex items-center gap-1.5">
                        <span className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" />
                        {(() => {
                          const needsAI = grouped.flatMap(g => g.items).filter(i => selectedIds.has(i.id)).length;
                          return needsAI > 0 ? 'Estimating...' : 'Creating batch...';
                        })()}
                      </span>
                    ) : (() => {
                      const needsAI = grouped.flatMap(g => g.items).filter(i => selectedIds.has(i.id)).length;
                      const preEstimated = estimatedItems.filter(i => selectedIds.has(i.id)).length;
                      if (needsAI > 0 && preEstimated > 0) return `Estimate ${needsAI} + include ${preEstimated} estimated`;
                      if (needsAI > 0) return `Estimate ${needsAI} item${needsAI !== 1 ? 's' : ''}`;
                      return `Submit ${preEstimated} estimated item${preEstimated !== 1 ? 's' : ''}`;
                    })()}
                  </button>
                </>
              )}
              {stage === 'review' && (
                <button
                  onClick={handleConfirmSubmit}
                  disabled={loading || editedTotal === 0}
                  className="px-4 py-1.5 text-xs font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <span className="flex items-center gap-1.5">
                      <span className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" />
                      Submitting...
                    </span>
                  ) : `Submit for approval (${editedTotal} SP)`}
                </button>
              )}
              {stage === 'submitted' && (
                <button
                  onClick={() => { onSubmitted(); onClose(); }}
                  className="px-4 py-1.5 text-xs font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Done
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BatchSubmitModal;
